"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useDebouncedValue } from "@tanstack/react-pacer";
import { Folder, MessagesSquare, Search } from "lucide-react";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useCommandSession } from "@/components/aios-command";
import { useCommandV2Search } from "@/hooks/use-command-v2-search";
import { formatRelativeTime } from "@/components/command-team-v2/utils/format-relative-time";
import { SEARCH_DEBOUNCE_WAIT } from "@/utils/global";
import { cn } from "@/lib/utils";

type SearchResult =
  | { kind: "project"; id: string; label: string }
  | {
      kind: "chat";
      id: string;
      label: string;
      updatedAt: string;
      inProject: boolean;
    };

type SearchRow =
  | { kind: "result"; result: SearchResult; index: number }
  | { kind: "label"; label: string };

interface SearchEverythingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onOpenChat: (sessionId: string) => void;
  onOpenProject: (projectId: string) => void;
}

function SearchResultSkeleton() {
  return (
    <div className="space-y-1 p-1.5">
      {Array.from({ length: 6 }).map((_, index) => (
        <div key={index} className="flex items-center gap-2.5 rounded-lg px-2.5 py-2">
          <Skeleton className="size-4 shrink-0 rounded" />
          <Skeleton className="h-4 flex-1" />
          <Skeleton className="h-3 w-12 shrink-0" />
        </div>
      ))}
    </div>
  );
}

export function SearchEverythingDialog({
  open,
  onOpenChange,
  onOpenChat,
  onOpenProject,
}: SearchEverythingDialogProps) {
  const { agentId } = useCommandSession();
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef<HTMLDivElement | null>(null);
  const [debouncedSearch] = useDebouncedValue(query, { wait: SEARCH_DEBOUNCE_WAIT });
  const trimmedQuery = query.trim();
  const debouncedTrimmed = debouncedSearch.trim();
  const isPendingSearch = trimmedQuery !== debouncedTrimmed;

  const { projects, sessions, isLoading, isFetching } = useCommandV2Search({
    agentId,
    search: debouncedTrimmed,
    enabled: open,
  });

  useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIndex(0);
    }
  }, [open]);

  const { rows, results } = useMemo<{
    rows: SearchRow[];
    results: SearchResult[];
  }>(() => {
    const rows: SearchRow[] = [];
    const results: SearchResult[] = [];
    const push = (result: SearchResult) => {
      rows.push({ kind: "result", result, index: results.length });
      results.push(result);
    };

    for (const project of projects) {
      const chats = sessions
        .filter((session) => session.projectId === project.id)
        .sort((a, b) => (b.updatedAt ?? "").localeCompare(a.updatedAt ?? ""));
      push({ kind: "project", id: project.id, label: project.name });
      for (const session of chats) {
        push({
          kind: "chat",
          id: session.id,
          label: session.title,
          updatedAt: session.updatedAt,
          inProject: true,
        });
      }
    }

    const others = sessions
      .filter((session) => !session.projectId)
      .sort((a, b) => (b.updatedAt ?? "").localeCompare(a.updatedAt ?? ""));
    if (others.length > 0) {
      if (rows.length > 0) rows.push({ kind: "label", label: "Other chats" });
      for (const session of others) {
        push({
          kind: "chat",
          id: session.id,
          label: session.title,
          updatedAt: session.updatedAt,
          inProject: false,
        });
      }
    }

    return { rows, results };
  }, [projects, sessions]);

  useEffect(() => {
    if (activeIndex > results.length - 1) setActiveIndex(0);
  }, [results.length, activeIndex]);

  const pick = (result: SearchResult) => {
    onOpenChange(false);
    if (result.kind === "chat") onOpenChat(result.id);
    else onOpenProject(result.id);
  };

  const moveActive = (delta: number) => {
    if (results.length === 0) return;
    const next = (activeIndex + delta + results.length) % results.length;
    setActiveIndex(next);
    listRef.current
      ?.querySelector(`[data-index="${next}"]`)
      ?.scrollIntoView({ block: "nearest" });
  };

  const showSkeleton =
    !isPendingSearch && (isLoading || (isFetching && results.length === 0));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl gap-0 overflow-hidden p-0">
        <DialogTitle className="sr-only">Search chats and projects</DialogTitle>
        <div className="flex items-center gap-2.5 border-b px-4 py-3 pr-12">
          <Search className="size-4 shrink-0 text-muted-foreground" />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "ArrowDown") {
                event.preventDefault();
                moveActive(1);
              } else if (event.key === "ArrowUp") {
                event.preventDefault();
                moveActive(-1);
              } else if (event.key === "Enter" && results[activeIndex]) {
                event.preventDefault();
                pick(results[activeIndex]);
              }
            }}
            placeholder="Search chats and projects"
            className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            autoFocus
          />
        </div>

        <div ref={listRef} className="app-scrollbar max-h-[380px] overflow-y-auto p-1.5">
          {showSkeleton ? (
            <SearchResultSkeleton />
          ) : results.length === 0 ? (
            <p className="px-3 py-8 text-center text-sm text-muted-foreground">
              {trimmedQuery
                ? `No chats or projects match “${trimmedQuery}”.`
                : "No chats or projects yet."}
            </p>
          ) : (
            rows.map((row) => {
              if (row.kind === "label") {
                return (
                  <p
                    key={`label-${row.label}`}
                    className="px-2.5 pb-1 pt-2.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"
                  >
                    {row.label}
                  </p>
                );
              }
              const { result, index } = row;
              return (
                <button
                  key={`${result.kind}-${result.id}`}
                  type="button"
                  data-index={index}
                  onClick={() => pick(result)}
                  onMouseMove={() => setActiveIndex(index)}
                  className={cn(
                    "flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm transition-colors",
                    result.kind === "chat" && result.inProject && "pl-9",
                    index === activeIndex && "bg-muted"
                  )}
                >
                  {result.kind === "project" ? (
                    <Folder className="size-4 shrink-0 text-muted-foreground" />
                  ) : (
                    <MessagesSquare className="size-4 shrink-0 text-muted-foreground" />
                  )}
                  <span className="min-w-0 flex-1 truncate">{result.label}</span>
                  {index === activeIndex ? (
                    <span className="shrink-0 rounded border border-border/60 px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                      Enter
                    </span>
                  ) : (
                    <span className="shrink-0 text-xs text-muted-foreground/80">
                      {result.kind === "project"
                        ? "Project"
                        : formatRelativeTime(result.updatedAt)}
                    </span>
                  )}
                </button>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
